import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  AlertIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  CloudIcon,
  DropletIcon,
  HardHatIcon,
  SunIcon,
  ThermometerIcon,
  WindIcon,
  WrenchIcon,
} from '@utils/icons';
import { fetchWeatherDay, fetchWeatherForecastRibbon } from '@apis/weather';
import { UnauthorizedError } from '@apis/auth';
import { useLang } from '@hooks/useLang';
import { useSelectedScheduleNumber } from '@hooks/useActiveSchedule';
import { weatherDayKey, weatherForecastKey } from '@constants/queryKeys';
import type {
  ActivityImpact,
  EquipmentAdvisory,
  RiskLevel,
  SafetyConsideration,
} from '@interfaces/IWeatherInsights';
import { ForecastRibbon } from './ForecastRibbon';
import { CONDITION_ICON } from '@utils/weatherIcons';
import { DashboardCard } from './_shared/DashboardCard';
import { WeatherShimmer, WeatherBodyShimmer, WeatherAssessmentShimmer } from '../DashboardSkeleton';
import styles from './weatherImpactCard.module.css';

const RISK_CLASS: Record<RiskLevel, string> = {
  low: styles.riskLow ?? '',
  medium: styles.riskMedium ?? '',
  high: styles.riskHigh ?? '',
};

interface IAllClearRowProps {
  label: string;
}

/** Positive empty state shown in a section when it has no items. */
function AllClearRow({ label }: IAllClearRowProps) {
  return (
    <li className={`${styles.sideRow} ${styles.sideRowClear}`}>
      <span className={styles.sideRowIcon}><CheckCircleIcon size={14} /></span>
      {label}
    </li>
  );
}

const ADVISORY_PAGE_SIZE = 3;
const ACTIVITY_PAGE_SIZE = 6;

// ── Paginated advisory list (safety + equipment side panels) ──────────────────

interface IPaginatedSafetyListProps {
  items: SafetyConsideration[];
  rowClassName: (row: SafetyConsideration) => string;
}

function PaginatedSafetyList({ items, rowClassName }: IPaginatedSafetyListProps) {
  const { t } = useTranslation();
  const lang = useLang();
  const [expanded, setExpanded] = useState(false);

  const visible = expanded ? items : items.slice(0, ADVISORY_PAGE_SIZE);
  const hiddenCount = items.length - ADVISORY_PAGE_SIZE;
  const showControl = items.length > ADVISORY_PAGE_SIZE;

  return (
    <>
      {visible.map((row, idx) => (
        <li key={idx} className={`${styles.sideRow} ${rowClassName(row)}`}>
          <span className={styles.sideRowIcon}><AlertIcon size={14} /></span>
          {row.text[lang]}
        </li>
      ))}
      {showControl && (
        <li className={styles.advisoryPagination}>
          <button
            type="button"
            className={styles.advisoryPageBtn}
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
          >
            {expanded
              ? t('dashboard.weatherImpact.showLess')
              : t('dashboard.weatherImpact.showMore', { count: hiddenCount })}
          </button>
        </li>
      )}
    </>
  );
}

interface IPaginatedEquipmentListProps {
  items: EquipmentAdvisory[];
}

function PaginatedEquipmentList({ items }: IPaginatedEquipmentListProps) {
  const { t } = useTranslation();
  const lang = useLang();
  const [expanded, setExpanded] = useState(false);

  const visible = expanded ? items : items.slice(0, ADVISORY_PAGE_SIZE);
  const hiddenCount = items.length - ADVISORY_PAGE_SIZE;
  const showControl = items.length > ADVISORY_PAGE_SIZE;

  return (
    <>
      {visible.map((row, idx) => (
        <li
          key={`${row.equipment}-${idx}`}
          className={`${styles.sideRow} ${row.severity === 'warning' ? styles.sideRowWarning : styles.sideRowInfo}`}
        >
          <span className={styles.sideRowIcon}><WrenchIcon size={14} /></span>
          <span>
            <span className={styles.advisoryEquipment}>{row.equipment}</span>
            {' '}
            {row.text[lang]}
          </span>
        </li>
      ))}
      {showControl && (
        <li className={styles.advisoryPagination}>
          <button
            type="button"
            className={styles.advisoryPageBtn}
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
          >
            {expanded
              ? t('dashboard.weatherImpact.showLess')
              : t('dashboard.weatherImpact.showMore', { count: hiddenCount })}
          </button>
        </li>
      )}
    </>
  );
}

// ── Paginated activity grid ───────────────────────────────────────────────────

interface IPaginatedActivityGridProps {
  activities: ActivityImpact[];
}

function PaginatedActivityGrid({ activities }: IPaginatedActivityGridProps) {
  const { t } = useTranslation();
  const lang = useLang();
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');

  const pageCount = Math.ceil(activities.length / ACTIVITY_PAGE_SIZE);
  const showControls = pageCount > 1;
  const start = page * ACTIVITY_PAGE_SIZE;
  const visible = activities.slice(start, start + ACTIVITY_PAGE_SIZE);

  const goPrev = () => {
    setDirection('prev');
    setPage((p) => Math.max(0, p - 1));
  };
  const goNext = () => {
    setDirection('next');
    setPage((p) => Math.min(pageCount - 1, p + 1));
  };

  return (
    <>
      <div className={styles.assessmentHead}>
        <h3 className={styles.sectionTitle}>{t('dashboard.weatherImpact.assessmentSectionTitle')}</h3>
        {showControls && (
          <div className={styles.activityNav}>
            <button
              type="button"
              className={styles.activityNavBtn}
              onClick={goPrev}
              disabled={page === 0}
              aria-label={t('dashboard.weatherImpact.prevPage')}
            >
              <span className={styles.navIconPrev}><ChevronRightIcon size={14} /></span>
            </button>
            <span className={styles.activityNavStatus}>
              {t('dashboard.weatherImpact.pageStatus', { current: page + 1, total: pageCount })}
            </span>
            <button
              type="button"
              className={styles.activityNavBtn}
              onClick={goNext}
              disabled={page === pageCount - 1}
              aria-label={t('dashboard.weatherImpact.nextPage')}
            >
              <ChevronRightIcon size={14} />
            </button>
          </div>
        )}
      </div>
      <div
        key={page}
        className={`${styles.tiles} ${direction === 'next' ? styles.slideNext : styles.slidePrev}`}
      >
        {visible.map((tile) => (
          <div key={tile.activityId} className={styles.tile}>
            <div className={styles.tileHead}>
              <span className={styles.tileName}>{tile.title}</span>
              <span className={`${styles.risk} ${RISK_CLASS[tile.risk]}`}>
                {t(`dashboard.weatherImpact.risk.${tile.risk}`)}
              </span>
            </div>
            <p className={styles.tileDetail}>{tile.reasonText[lang]}</p>
            {tile.action && <p className={styles.tileAction}>→ {tile.action[lang]}</p>}
            {tile.timeframe && <span className={styles.tileWhen}>⏱ {tile.timeframe[lang]}</span>}
          </div>
        ))}
      </div>
    </>
  );
}

// ── Main card ─────────────────────────────────────────────────────────────────

// gcTime keeps an inactive day's entry alive so revisiting a previously-clicked
// day renders instantly; staleTime tracks the backend's 30-min weather cache.
// Module-scope constants — not reactive, so they don't belong in the render body.
const STALE_TIME = 30 * 60 * 1000; // 30m — matches WEATHER_CACHE_TTL_MS
const GC_TIME = 4 * 60 * 60 * 1000; // 4h — matches OPENROUTER_CACHE_TTL_MS

export function WeatherImpactCard() {
  const { t } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();

  // The user's raw schedule pin (Redux, else persisted last-viewed) — the
  // backend validates and degrades a stale pin, so no list fetch is needed
  // here (same pattern as the Portfolio).
  const scheduleNumber = useSelectedScheduleNumber(siteId);

  // No client-side timezone: the backend owns "today" (the site/project tz). On
  // first load selectedDate is undefined → the backend defaults to its own today;
  // the response's `selectedDate` is the authoritative selected day thereafter.
  const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined);

  // Two independent server-state queries (Part 3). The cheap ribbon and the heavy
  // AI day body load on their own clocks, so the ribbon can render before — and
  // the day body the moment — each resolves. STALE_TIME/GC_TIME are module-scope
  // (see top of file) so they aren't re-created each render.
  const forecastQuery = useQuery({
    queryKey: weatherForecastKey(siteId),
    queryFn: ({ signal }) => fetchWeatherForecastRibbon(siteId!, signal),
    enabled: Boolean(siteId),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });

  const dayQuery = useQuery({
    queryKey: weatherDayKey(siteId, scheduleNumber, selectedDate),
    queryFn: ({ signal }) => fetchWeatherDay(siteId!, selectedDate, scheduleNumber ?? undefined, signal),
    enabled: Boolean(siteId),
    // Keep the previous day's response while a newly-selected day loads, so only the
    // body below reloads while the ribbon stays static + clickable.
    placeholderData: keepPreviousData,
    // A degraded "schedule unavailable" body is a successful response, so it would
    // otherwise sit fresh for the full staleTime — after the user reconnects Genba
    // and returns, the card would still show the stale prompt. Mark that state
    // immediately stale so it refetches on the next mount (i.e. on return from the
    // reconnect flow) and clears once Genba is back. A normal body keeps STALE_TIME.
    staleTime: (query) => (query.state.data?.scheduleUnavailable ? 0 : STALE_TIME),
    gcTime: GC_TIME,
  });

  const forecast = forecastQuery.data?.forecast ?? [];
  const day = dayQuery.data;

  // First load — nothing to show yet from either query.
  const isLoading = forecastQuery.isLoading && dayQuery.isLoading;
  // The day fetch failed (placeholderData may still hold a stale day — the error
  // takes precedence, so the selected day shows an error rather than a hung shimmer).
  const isError = dayQuery.isError;
  const error = dayQuery.error;
  // True while a freshly-selected day is loading (previous day still shown).
  const bodyLoading = dayQuery.isPlaceholderData;
  // A background refetch of a stale "schedule unavailable" body — e.g. just back
  // from reconnecting Genba. Shimmer ONLY the activities column during that
  // window (safety + equipment stay static — their data is still valid and Genba-
  // independent) so the now-stale connect prompt doesn't flash before activities
  // arrive. A normal body refreshes silently (no shimmer) — only the degraded one.
  const activitiesRefetching = dayQuery.isFetching && Boolean(day?.scheduleUnavailable);

  // Auth failures and message-less errors get the generic copy; everything else
  // surfaces the backend message. Shared by the full-card and inline error states.
  const errorMessage =
    error instanceof UnauthorizedError || !(error instanceof Error) || !error.message
      ? t('dashboard.weatherImpact.loadFailed')
      : t('dashboard.weatherImpact.errorWithSupport', { message: error.message });

  const ConditionIcon = day ? (CONDITION_ICON[day.conditions.conditionCode] ?? SunIcon) : SunIcon;
  // The first forecast day is "today". Equipment is live on-site presence, so the
  // equipment advisory is only meaningful when viewing today.
  const isToday = !!day && day.selectedDate === forecast[0]?.date;

  return (
    <DashboardCard
      title={t('dashboard.weatherImpact.title')}
      titleIcon={<span className={styles.titleIconInfo}><CloudIcon /></span>}
      subtitle={t('dashboard.weatherImpact.subtitle')}
      headerRight={
        day ? (
          <div className={styles.conditionsRow}>
            <span className={styles.condition}>
              <ConditionIcon /> {t(`dashboard.weatherImpact.condition.${day.conditions.conditionCode}`)}
            </span>
            <span className={styles.condition}><ThermometerIcon /> {day.conditions.tempC}°C</span>
            <span className={styles.condition}><DropletIcon /> {day.conditions.humidityPct}%</span>
            <span className={styles.condition}><WindIcon /> {day.conditions.windKmh} km/h</span>
          </div>
        ) : null
      }
      bodyClassName={day || forecast.length > 0 ? styles.body : isLoading ? undefined : styles.bodyEmpty}
    >
      {isLoading ? (
        // First load — neither query has data yet. Show the full-card shimmer.
        <WeatherShimmer />
      ) : !day && !forecast.length ? (
        // No data at all (first-load error with no previous data).
        <p className={styles.stateMsg}>{errorMessage}</p>
      ) : (
        <>
          {/* Forecast ribbon renders independently of the day body — it appears as
              soon as the cheap forecast query resolves. Highlight follows the user's
              click immediately (local selectedDate), falling back to the backend's
              selectedDate on first load. */}
          {forecast.length > 0 && (
            <ForecastRibbon
              days={forecast}
              selectedDate={selectedDate ?? day?.selectedDate ?? forecast[0]!.date}
              onSelect={setSelectedDate}
            />
          )}

          {isError ? (
            // Day fetch failed — show an inline error below the ribbon instead of a
            // hung shimmer. Takes precedence over the placeholder: with
            // keepPreviousData a failed day-switch still holds a stale `day`, so
            // gating on `!day` here would fall through to an infinite body shimmer.
            <div className={styles.bodyLoading}>
              <p className={styles.stateMsg}>{errorMessage}</p>
            </div>
          ) : bodyLoading || !day ? (
            // Day body still loading (or a freshly-selected day swapping in) — the
            // ribbon stays static above while only the body below shimmers.
            <div className={styles.bodyLoading}>
              <WeatherBodyShimmer />
            </div>
          ) : (
            <>
              {/* LEFT — Activity Impact Assessment grid */}
              <section className={styles.assessment}>
                {activitiesRefetching ? (
                  // Re-fetching a stale "unavailable" body (e.g. just reconnected
                  // Genba) — shimmer only this column; the side panels stay put.
                  <WeatherAssessmentShimmer />
                ) : day.scheduleUnavailable ? (
                  // Genba needs (re)connection — activities couldn't load. Reconnect
                  // is handled globally by the TopBar (visible here), so this is just
                  // an honest "data unavailable" message, no in-page reconnect banner.
                  <>
                    <div className={styles.assessmentHead}>
                      <h3 className={styles.sectionTitle}>{t('dashboard.weatherImpact.assessmentSectionTitle')}</h3>
                    </div>
                    <p className={styles.emptyLine}>{t('dashboard.weatherImpact.scheduleUnavailable')}</p>
                  </>
                ) : day.activityImpacts.length > 0 ? (
                  <PaginatedActivityGrid activities={day.activityImpacts} />
                ) : (
                  <>
                    <div className={styles.assessmentHead}>
                      <h3 className={styles.sectionTitle}>{t('dashboard.weatherImpact.assessmentSectionTitle')}</h3>
                    </div>
                    <p className={styles.emptyLine}>{t('dashboard.weatherImpact.noActivityForDay')}</p>
                  </>
                )}
              </section>

              {/* RIGHT — Safety & Equipment sidebars */}
              <aside className={styles.side}>
                <div className={styles.sidePanel}>
                  <h3 className={styles.sidePanelTitle}>
                    <HardHatIcon /> {t('dashboard.weatherImpact.safetyConsiderations')}
                  </h3>
                  <ul className={styles.sideList}>
                    {day.safetyConsiderations.length > 0 ? (
                      <PaginatedSafetyList
                        items={day.safetyConsiderations}
                        rowClassName={(row) =>
                          row.severity === 'warning' ? (styles.sideRowWarning ?? '') : (styles.sideRowInfo ?? '')
                        }
                      />
                    ) : (
                      <AllClearRow label={t('dashboard.weatherImpact.allClear')} />
                    )}
                  </ul>
                </div>
                {/* Equipment is live on-site presence — only meaningful for today. */}
                {isToday && (
                  <div className={styles.sidePanel}>
                    <h3 className={styles.sidePanelTitle}>
                      <WrenchIcon /> {t('dashboard.weatherImpact.equipmentAdvisory')}
                    </h3>
                    <ul className={styles.sideList}>
                      {day.equipmentAdvisories.length > 0 ? (
                        <PaginatedEquipmentList items={day.equipmentAdvisories} />
                      ) : (
                        <AllClearRow label={t('dashboard.weatherImpact.allClear')} />
                      )}
                    </ul>
                  </div>
                )}
              </aside>
              <p className={styles.disclaimer}>{t('dashboard.weatherImpact.aiDisclaimer')}</p>
            </>
          )}
        </>
      )}
    </DashboardCard>
  );
}
